import {AssigneeRepositoryPicker} from '@github-ui/item-picker/AssigneePicker'

import {AlertIcon, TableIcon} from '@primer/octicons-react'
import {Box} from '@primer/react'
import {Suspense, useCallback, useEffect} from 'react'
import {MetadataLoading} from './MetadataLoading'
import {useIssueCreateDataContext} from './contexts/IssueCreateDataContext'
import {useQueryLoader} from 'react-relay'
import {useIssueCreateConfigContext} from './contexts/IssueCreateConfigContext'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {MilestonesPickerGraphqlQuery, type Milestone, MilestonePickerBase} from '@github-ui/item-picker/MilestonePicker'
import type {MilestonePickerQuery} from '@github-ui/item-picker/MilestonePickerQuery.graphql'
import {LabelPickerGraphqlQuery, LabelPickerBase} from '@github-ui/item-picker/LabelPicker'
import type {LabelPickerQuery} from '@github-ui/item-picker/LabelPickerQuery.graphql'
import {ProjectPickerGraphqlQuery, ProjectPicker, type Project} from '@github-ui/item-picker/ProjectPicker'
import type {ProjectPickerQuery} from '@github-ui/item-picker/ProjectPickerQuery.graphql'
import {SharedPicker} from '@github-ui/item-picker/SharedPicker'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {CreateIssueMilestonesSection} from '@github-ui/issue-metadata/MilestonesSection'
import {CreateIssueAssigneesSection} from '@github-ui/issue-metadata/AssigneesSection'
import {CreateIssueLabelsSection} from '@github-ui/issue-metadata/LabelsSection'

export const MetadataSelectors = () => {
  const {optionConfig} = useIssueCreateConfigContext()
  const createMetadataSidepanelEnabled = useFeatureFlag('issues_react_create_metadata_sidepanel')
  const showMetadataInSections = !optionConfig.insidePortal && createMetadataSidepanelEnabled
  const {repository, labels, setLabels, assignees, setAssignees, projects, setProjects, milestone, setMilestone} =
    useIssueCreateDataContext()

  const canAssign = repository?.viewerIssueCreationPermissions?.assignable
  const canLabel = repository?.viewerIssueCreationPermissions?.labelable
  const canMilestone = repository?.viewerIssueCreationPermissions?.milestoneable
  const canProject = repository?.viewerIssueCreationPermissions?.triageable

  const sharedConfigProps = {
    insidePortal: optionConfig.insidePortal,
    shortcutEnabled: optionConfig.singleKeyShortcutsEnabled,
    shortcutHintVisible: false,
  }

  const [labelsRef, loadLabels, disposeLabels] = useQueryLoader<LabelPickerQuery>(LabelPickerGraphqlQuery)
  const [projectsRef, loadProjects, disposeProjects] = useQueryLoader<ProjectPickerQuery>(ProjectPickerGraphqlQuery)
  const [milestonesRef, loadMilestones, disposeMilestones] =
    useQueryLoader<MilestonePickerQuery>(MilestonesPickerGraphqlQuery)

  useEffect(() => {
    if (repository) {
      loadLabels({owner: repository.owner.login, repo: repository.name}, {fetchPolicy: 'store-or-network'})
      loadProjects({owner: repository.owner.login, repo: repository.name}, {fetchPolicy: 'store-or-network'})
      loadMilestones({owner: repository.owner.login, repo: repository.name}, {fetchPolicy: 'store-or-network'})
    }
    return () => {
      disposeLabels()
      disposeProjects()
      disposeMilestones()
    }
  }, [repository, disposeLabels, disposeMilestones, loadLabels, loadMilestones, disposeProjects, loadProjects])

  const setMilestoneCallback = useCallback(
    (milestones: Milestone[] | null) => {
      setMilestone(milestones?.[0] || null)
    },
    [setMilestone],
  )

  return (
    <Suspense fallback={<MetadataLoading />}>
      <Box sx={{display: 'flex', flexDirection: showMetadataInSections ? 'column' : 'row', gap: 2, flexWrap: 'wrap'}}>
        {showMetadataInSections && <h2 className="sr-only">Metadata</h2>}
        {repository &&
          (canAssign || assignees.length > 0) &&
          (showMetadataInSections ? (
            <CreateIssueAssigneesSection
              sx={{flexGrow: 1}}
              repoName={repository.name}
              repoOwner={repository.owner.login}
              readonly={!canAssign}
              assignees={assignees}
              onSelectionChange={setAssignees}
              {...sharedConfigProps}
            />
          ) : (
            <AssigneeRepositoryPicker
              repoName={repository.name}
              repoOwner={repository.owner.login}
              readonly={!canAssign}
              assignees={assignees}
              participants={[]}
              onSelectionChange={setAssignees}
              {...sharedConfigProps}
            />
          ))}
        {labelsRef &&
          (canLabel || labels.length > 0) &&
          (showMetadataInSections ? (
            <CreateIssueLabelsSection
              readonly={!canLabel}
              labels={labels}
              labelQueryRef={labelsRef}
              onSelectionChange={setLabels}
              {...sharedConfigProps}
            />
          ) : (
            <LabelPickerBase
              readonly={!canLabel}
              labels={labels}
              labelQueryRef={labelsRef}
              onSelectionChange={setLabels}
              {...sharedConfigProps}
            />
          ))}
        {projectsRef && repository && !optionConfig.scopedProjectTitle && (canProject || projects.length > 0) && (
          <ErrorBoundary
            fallback={<SharedPicker anchorText={'Projects are unavailable'} leadingIcon={AlertIcon} readonly />}
          >
            <ProjectPicker
              pickerId="create-issue-projects-picker"
              projectQueryRef={projectsRef}
              readonly={!canProject}
              onSave={(selectedProjects: Project[]) => {
                setProjects(selectedProjects)
              }}
              selectedProjects={projects}
              firstSelectedProjectTitle={projects.length > 0 ? projects[0]!.title : ''}
              owner={repository.owner.login}
              repo={repository.name}
              includeClassicProjects={true}
              {...sharedConfigProps}
            />
          </ErrorBoundary>
        )}
        {projectsRef && optionConfig.scopedProjectTitle && (
          <SharedPicker anchorText={optionConfig.scopedProjectTitle} leadingIcon={TableIcon} readonly />
        )}
        {milestonesRef &&
          (canMilestone || milestone !== null) &&
          (showMetadataInSections ? (
            <CreateIssueMilestonesSection
              sx={{flexGrow: 1}}
              activeMilestone={milestone}
              milestonesRef={milestonesRef}
              onSelectionChange={setMilestoneCallback}
              viewerCanSetMilestone={canMilestone ?? false}
              {...sharedConfigProps}
            />
          ) : (
            <MilestonePickerBase
              readonly={!canMilestone}
              activeMilestone={milestone}
              milestoneQueryRef={milestonesRef}
              onSelectionChange={setMilestoneCallback}
              {...sharedConfigProps}
            />
          ))}
      </Box>
    </Suspense>
  )
}

try{ MetadataSelectors.displayName ||= 'MetadataSelectors' } catch {}